import React, { useCallback, useRef, useState } from 'react';
import ModalDialog from '../../../../components/ModalDialog';
import Button from '../../../../components/Button';
import EventForm, { EventFormRef } from './EventForm';
import { CreateEventFormData, CreateEventModalProps } from './types';
import { useEventMutation } from '../hooks/useEventMutations';
import { getDefaultEventFormValues } from '../utils/formUtils';

const CreateEventModal: React.FC<CreateEventModalProps> = ({
  isOpen,
  onClose,
  selectInfo,
  calendarId,
  onEventCreated,
}) => {
  const formRef = useRef<EventFormRef>(null);
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultValues = getDefaultEventFormValues(selectInfo);
  const { handleCreateEvent } = useEventMutation();

  const handleSubmit = async (data: CreateEventFormData) => {
    const result = await handleCreateEvent(data, calendarId);
    if (result.success) {
      onEventCreated?.(result.event);
      onClose();
    }
  };

  const handlePrimaryAction = () => {
    if (isSubmittingRef.current) return;
    if (!formRef.current) return;
    isSubmittingRef.current = true;
    formRef.current.submit();
  };

  const handleSubmittingChange = useCallback((submitting: boolean) => {
    setIsSubmitting(submitting);
    if (!submitting) isSubmittingRef.current = false;
  }, []);

  return (
    <ModalDialog
      isOpen={isOpen}
      setClosed={onClose}
      title="Create Event"
      width="small"
      overflow="auto"
      fullHeight
      primaryActionOnClick={handlePrimaryAction}
      primaryActionOnEnter
      rightFooter={
        <Button
          title="Create Event"
          onClick={handlePrimaryAction}
          disabled={isSubmitting}
        />
      }
      headerClassName="py-2"
      bodyClassName="pt-4"
    >
      <EventForm
        ref={formRef}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isAllDay={defaultValues.isAllDay}
        onSubmittingChange={handleSubmittingChange}
        onValidationFailed={() => {
          isSubmittingRef.current = false;
        }}
        onSubmitRequest={handlePrimaryAction}
      />
    </ModalDialog>
  );
};

export default CreateEventModal;
